import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import L from "leaflet";
import { ImageOverlay, MapContainer, useMap } from "react-leaflet";
import {
  mapCoordinate,
  getPlantMapImageUrl,
  handleScrollZoom
} from "../util/helper/helperFunctions";
import { AppContext } from "../Context/Context";
import zoomInIcon from "../assets/icons/add.png";
import zoomOutIcon from "../assets/icons/minus.png";
import zoomRefreshIcon from "../assets/icons/refresh.png";
import YardSummary from "../components/Modal/YardSummary/YardSummary";
import YardDetails from "../components/Modal/YardDetails/YardDetails";
import YardDockDoors from "../components/YardView/YardDockDoors";
import { yardLegends } from "../util/helper/constants";
import LegendsModal from "../components/Modal/LegendsModal";
import "../assets/css/Map/map.css";
import "../assets/css/YardView/YardView.css";

const zoomTransformArr = Array.from(
  { length: 21 },
  (_, i) => `scale(${1 + i * 0.2}) translate(0)`
);

// Fix for map centering glitch at 50% and lesser browser zoom level on inital render.
const RecenterMap = () => {
  const map = useMap();

  useEffect(() => {
    map.invalidateSize();
  }, [map]);

  return null;
};

const YardView = () => {
  const { plantCode } = useParams();
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const accumulatedDeltaRef = useRef(0);

  const { plantConfig, appConfig } = useContext(AppContext);

  const { yardSummaryModalIsOpen, isYardLegendsModalOpen, yardObjectsRawData } =
    useSelector((state) => state.yard);

  const [mapObject, setMapObject] = useState(null);
  const [mapLoader, setMapLoader] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [yardData, setYardData] = useState(null);
  const [legendColors, setLegendColors] = useState(null);
  const [yardDetailModalData, setYardDetailModalData] = useState(null);
  const [isYardDetailModalOpen, setIsYardDetailModalOpen] = useState(false);

  useEffect(() => {
    mapObject?.map?.target?.doubleClickZoom?.disable();
  }, [mapObject]);

  useEffect(() => {
    if (!mapObject) return;
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      setZoomLevel(screenWidth >= 2560 ? 4 : screenWidth >= 1920 ? 1 : 0);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [mapObject]);

  useEffect(() => {
    if (!mapObject) return;
    document.getElementById("yard-view-map-id-reference").style.transform =
      zoomTransformArr[zoomLevel];
  }, [zoomLevel, mapObject]);

  useEffect(() => {
    const colors = yardLegends.reduce((acc, { statusColors }) => {
      statusColors.forEach(({ label, value }) => (acc[label] = value));
      return acc;
    }, {});
    setLegendColors(colors);
  }, []);

  const combineDataByLocation = (data = []) => {
    const combined = {};
    data.forEach(({ locationName, deliveryNumber, ...rest }) => {
      if (!combined[locationName]) {
        combined[locationName] = {
          ...rest,
          locationName,
          deliveryNumber: deliveryNumber || ""
        };
      } else if (deliveryNumber) {
        combined[locationName].deliveryNumber += combined[locationName]
          .deliveryNumber
          ? `, ${deliveryNumber}`
          : deliveryNumber;
      }
    });
    return Object.values(combined);
  };

  useEffect(() => {
    if (yardObjectsRawData) {
      setYardData(combineDataByLocation(yardObjectsRawData));
      setMapLoader(false);
    }
  }, [yardObjectsRawData]);

  const resetZoom = () => {
    setZoomLevel(0);
    mapRef.current?.setView([0, 0], 0);
  };

  const RenderMap = useMemo(() => {
    if (!appConfig || !mapObject || !plantConfig || !plantCode) return null;
    const img = getPlantMapImageUrl(appConfig, plantCode, "YARD_MAP");
    const {
      yard: {
        mapBg: {
          bounds: { startX, startY, endX, endY }
        }
      }
    } = plantConfig;

    const topLeft = mapCoordinate(startX, startY, "yardmap");
    const bottomRight = mapCoordinate(endX, endY, "yardmap");
    const bounds = [
      [
        mapObject.map.target.unproject(L.point(topLeft.x, topLeft.y)).lat,
        mapObject.map.target.unproject(L.point(topLeft.x, topLeft.y)).lng
      ],
      [
        mapObject.map.target.unproject(L.point(bottomRight.x, bottomRight.y))
          .lat,
        mapObject.map.target.unproject(L.point(bottomRight.x, bottomRight.y))
          .lng
      ]
    ];

    return <ImageOverlay fetchPriority="high" url={img} bounds={bounds} />;
  }, [mapObject, plantConfig, appConfig, plantCode]);

  useEffect(() => {
    mapObject && mapObject.map.target.doubleClickZoom.disable();

    const mapContainer = mapContainerRef.current;

    if (mapObject && mapContainer) {
      mapContainer.addEventListener("wheel", (event) =>
        handleScrollZoom({
          accumulatedDeltaRef,
          event,
          zoomScales: zoomTransformArr,
          setZoomLevel
        })
      );

      return () => {
        mapContainer.removeEventListener("wheel", (event) =>
          handleScrollZoom({
            accumulatedDeltaRef,
            event,
            zoomScales: zoomTransformArr,
            setZoomLevel
          })
        );
      };
    }
  }, [mapObject]);

  return (
    <div className={`map-container-wrapper plant-${plantCode}`}>
      <div
        id="yard-view-map-container-id"
        className="map-container"
        ref={mapContainerRef}
      >
        {mapLoader && (
          <div className="loader">
            <h4>Loading Yard View...</h4>
            <div className="spinner"></div>
          </div>
        )}
        <MapContainer
          id="yard-view-map-id-reference"
          style={{
            height: "100%",
            width: "100%",
            borderRadius: "10px",
            background: "green",
            transition: "transform 0.5s ease-out"
          }}
          center={[0, 0]}
          zoom={0}
          ref={mapRef}
          maxBoundsViscosity={1.0}
          crs={L.CRS.Simple}
          attributionControl={false}
          scrollWheelZoom={false}
          zoomControl={false}
          disableDoubleClickZoom={false}
          whenReady={(map) => setMapObject({ map })}
        >
          {legendColors && (
            <YardDockDoors
              yardData={yardData}
              legendColors={legendColors}
              mapObject={mapObject}
              setIsYardDetailModalOpen={setIsYardDetailModalOpen}
              setYardDetailModalData={setYardDetailModalData}
            />
          )}
          {RenderMap}
          <RecenterMap />
        </MapContainer>
      </div>

      <div className="zoom-control-center">
        <button
          id="map-zoom-in-btn"
          className="zoom-controls zoom-in-control"
          disabled={zoomLevel >= 20}
          onClick={() => setZoomLevel((z) => z + 1)}
        >
          <img alt="zoom-in-icon" src={zoomInIcon} />
        </button>
        <button
          id="map-zoom-out-btn"
          className="zoom-controls zoom-out-control"
          disabled={zoomLevel < 1}
          onClick={() => setZoomLevel((z) => z - 1)}
        >
          <img
            alt="zoom-out-icon"
            style={{ opacity: zoomLevel < 1 ? "0.5" : "1" }}
            src={zoomOutIcon}
          />
        </button>
        <button
          className="zoom-controls zoom-reset-controls"
          disabled={zoomLevel === 0}
          onClick={resetZoom}
        >
          <img
            alt="zoom-refresh-icon"
            style={{ opacity: zoomLevel === 0 ? "0.5" : "1" }}
            src={zoomRefreshIcon}
          />
        </button>
      </div>

      {isYardDetailModalOpen && (
        <YardDetails
          yardData={yardDetailModalData}
          setIsYardDetailModalOpen={setIsYardDetailModalOpen}
        />
      )}
      {yardSummaryModalIsOpen && <YardSummary />}
      {isYardLegendsModalOpen && <LegendsModal data={yardLegends} />}
    </div>
  );
};

export default YardView;
